
import { useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Topic, categories } from "@/types/learning-path";

export function useLearningPath() {
  const { data: performanceData, isLoading: isLoadingPerformance } = useQuery({
    queryKey: ['performance_history'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('performance_history')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (error) {
        console.error('Error fetching performance:', error);
        return null;
      }

      return data;
    }
  });

  // Define getTopicStatus first before using it in other functions
  const getTopicStatus = useCallback((topic: Topic) => {
    // Main categories are always unlocked and in-progress
    if (!topic.id.includes('.')) {
      return topic.id === "1" ? "in-progress" : "locked";
    }

    if (!performanceData || performanceData.length === 0) {
      // First subcategory is unlocked by default
      return topic.id === "1.1" ? "in-progress" : "locked";
    }

    // Match both category and subject for more specific targeting
    const relevantScores = performanceData.filter(p => 
      p.category === topic.category || p.subject === topic.subject
    );

    if (relevantScores.length === 0) {
      // Check if it's the first subcategory of any main category
      const [mainCat, subCat] = topic.id.split('.');
      return subCat === "1" && mainCat === "1" ? "in-progress" : "locked";
    }

    const averageScore = relevantScores.reduce((acc, curr) => acc + curr.score, 0) / relevantScores.length;
    
    if (averageScore >= topic.minScore) {
      return "completed";
    } else if (averageScore > 0) {
      return "in-progress";
    }

    // For subcategories, check if previous subcategory is completed
    const [mainCat, subCat] = topic.id.split('.');
    const prevSubId = `${mainCat}.${parseInt(subCat) - 1}`;
    const prevSub = categories
      .find(c => c.id === mainCat)
      ?.subcategories
      ?.find(s => s.id === prevSubId);

    if (!prevSub || getTopicStatus(prevSub) === "completed") {
      return "in-progress";
    }

    return "locked";
  }, [performanceData]);

  // Calculate overall progress
  const overallProgress = useMemo(() => {
    if (!performanceData || performanceData.length === 0) return 0;
    
    // Count completed topics
    let completedCount = 0;
    let totalCount = 0;
    
    categories.forEach(category => {
      // Count main category
      totalCount++;
      if (getTopicStatus(category) === "completed") {
        completedCount++;
      }
      
      // Count subcategories if they exist
      if (category.subcategories) {
        category.subcategories.forEach(sub => {
          totalCount++;
          if (getTopicStatus(sub) === "completed") {
            completedCount++;
          }
        });
      }
    });
    
    return totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  }, [performanceData, getTopicStatus]);

  // Get recent performance stats
  const recentPerformance = useMemo(() => {
    if (!performanceData || performanceData.length === 0) return [];
    
    // Get last 5 performance entries
    return performanceData.slice(0, 5).map(entry => ({
      subject: entry.subject || 'Unknown',
      score: entry.score || 0,
      date: new Date(entry.date).toLocaleDateString(),
      category: entry.category || 'General'
    }));
  }, [performanceData]);

  return { 
    getTopicStatus, 
    performanceData, 
    isLoadingPerformance,
    overallProgress,
    recentPerformance 
  };
}
